/**
 * RankingScene — 리뉴얼된 랭킹 화면
 */
import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';
import { getRankings, getMyRanking, getPlayerId, type RankingEntry } from '../api';

export class RankingScene extends Phaser.Scene {
  private loadingText!: Phaser.GameObjects.Text;
  private panelContainer!: Phaser.GameObjects.Container;

  constructor() {
    super({ key: 'RankingScene' });
  }

  create(): void {
    // ── 배경 (우주/밤하늘 분위기) ──
    const bg = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'bg_space');
    bg.setDisplaySize(GAME_WIDTH, GAME_HEIGHT);

    // 반짝이는 별 효과 추가
    this.createStars();

    // ── 타이틀 ──
    const titleContainer = this.add.container(GAME_WIDTH / 2, 60);
    const titleBg = this.add.graphics();
    titleBg.fillStyle(0xFFFFFF, 0.1);
    titleBg.fillRoundedRect(-120, -25, 240, 50, 25);
    titleBg.lineStyle(2, 0xFFFFFF, 0.2);
    titleBg.strokeRoundedRect(-120, -25, 240, 50, 25);
    
    const titleTxt = this.add.text(0, 0, '🏆 GLOBAL RANKING', {
      fontFamily: '"Noto Sans KR", "Outfit", sans-serif',
      fontSize: '24px',
      fontStyle: 'bold',
      color: '#FFD700',
    }).setOrigin(0.5);
    titleContainer.add([titleBg, titleTxt]);

    // ── 랭킹 판넬 ──
    this.panelContainer = this.add.container(0, 0);
    
    // 로딩 메시지
    this.loadingText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '데이터를 불러오는 중...', {
      fontFamily: '"Noto Sans KR", sans-serif',
      fontSize: '16px',
      color: '#DDEEFF',
    }).setOrigin(0.5);

    // ── 뒤로가기 버튼 ──
    this.createBackButton();

    // 데이터 로드
    this.loadData();
  }

  private createStars(): void {
    for (let i = 0; i < 30; i++) {
      const x = Math.random() * GAME_WIDTH;
      const y = Math.random() * GAME_HEIGHT;
      const star = this.add.circle(x, y, Math.random() * 2, 0xFFFFFF, 0.4);
      this.tweens.add({
        targets: star,
        alpha: 0.1,
        duration: 800 + Math.random() * 1000,
        yoyo: true,
        repeat: -1
      });
    }
  }

  private async loadData(): Promise<void> {
    try {
      const [rankData, myData] = await Promise.all([
        getRankings(20),
        getMyRanking(),
      ]);

      this.loadingText.destroy();
      this.renderList(rankData.rankings);
      if (myData && myData.rank) {
        this.renderMyRank(myData.rank, myData.best_score);
      }
    } catch (e) {
      this.loadingText.setText('서버와 연결할 수 없습니다');
    }
  }

  private renderList(rankings: RankingEntry[]): void {
    const listY = 140;
    const itemH = 54;
    const margin = 20;
    const width = GAME_WIDTH - (margin * 2);

    rankings.slice(0, 10).forEach((item, i) => {
      const y = listY + (i * itemH);
      const isMe = item.player_id === getPlayerId();

      // 줄 아이템 배경
      const row = this.add.graphics();
      const rowAlpha = isMe ? 0.25 : 0.15;
      row.fillStyle(0xFFFFFF, rowAlpha);
      row.fillRoundedRect(margin, y - 25, width, itemH - 6, 15);
      
      if (isMe) {
          row.lineStyle(2, 0xFFD700, 0.6);
          row.strokeRoundedRect(margin, y - 25, width, itemH - 6, 15);
      }

      // 순위 표시 (상위 3명 특수 처리)
      let rankColor = '#FFFFFF';
      let rankText = `${item.rank}`;
      
      if (item.rank === 1) {
        rankText = '🥇';
        const glow = this.add.circle(margin + 25, y, 15, 0xFFD700, 0.2).setDepth(1);
      } else if (item.rank === 2) {
        rankText = '🥈';
      } else if (item.rank === 3) {
        rankText = '🥉';
      } else {
        rankColor = '#AABBDD';
      }

      this.add.text(margin + 25, y, rankText, {
        fontFamily: '"Outfit", sans-serif',
        fontSize: '20px',
        fontStyle: 'bold',
        color: rankColor
      }).setOrigin(0.5);
      
      // 닉네임
      this.add.text(margin + 55, y, item.nickname, { 
        fontFamily: '"Noto Sans KR", sans-serif',
        fontSize: '16px', 
        color: isMe ? '#FFE4F0' : '#FFFFFF',
        fontStyle: isMe ? 'bold' : 'normal'
      }).setOrigin(0, 0.5);

      // 점수
      this.add.text(GAME_WIDTH - margin - 20, y, item.best_score.toLocaleString(), {
        fontFamily: '"Outfit", sans-serif',
        fontSize: '18px',
        fontStyle: 'bold',
        color: item.rank === 1 ? '#FFD700' : (isMe ? '#FFB6C1' : '#FFFFFF')
      }).setOrigin(1, 0.5);
    });

    if (rankings.length === 0) {
      this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '아직 도전자가 없어요!\n첫 번째 코멧슈터가 되어보세요! ✨', {
        fontFamily: '"Noto Sans KR", sans-serif',
        fontSize: '15px',
        color: '#AABBDD',
        align: 'center',
        lineSpacing: 10
      }).setOrigin(0.5);
    }
  }

  private renderMyRank(rank: number, score: number): void {
    const barH = 70;
    const y = GAME_HEIGHT - 120;
    
    const bg = this.add.graphics();
    bg.fillStyle(0xAA77DD, 0.2);
    bg.fillRoundedRect(15, y - barH/2, GAME_WIDTH - 30, barH, 20);
    bg.lineStyle(2, 0xFFFFFF, 0.2);
    bg.strokeRoundedRect(15, y - barH/2, GAME_WIDTH - 30, barH, 20);

    this.add.text(40, y, `내 순위: #${rank}`, { fontSize: '18px', fontStyle: 'bold', color: '#FFE4F0' }).setOrigin(0, 0.5);
    this.add.text(GAME_WIDTH - 40, y, `${score.toLocaleString()} pts`, { fontSize: '20px', fontStyle: 'bold', color: '#FFD700' }).setOrigin(1, 0.5);
  }

  private createBackButton(): void {
    const btn = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 45, '닫기', {
      fontSize: '18px',
      color: '#FFF',
      backgroundColor: '#445577',
      padding: { x: 40, y: 10 }
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    
    btn.on('pointerdown', () => this.scene.start('MenuScene'));
  }
}
